import { useState, useImperativeHandle, forwardRef, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X, Images } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

export interface EventGalleryLightboxHandle {
  open: (startIndex?: number) => void;
}

interface EventGalleryLightboxProps {
  images: string[];
  coverImage?: string | null;
  /** Show grid thumbnails and "Se alla" button externally */
  showThumbnails?: boolean;
}

export const EventGalleryLightbox = forwardRef<EventGalleryLightboxHandle, EventGalleryLightboxProps>(
  ({ images, coverImage, showThumbnails = true }, ref) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const touchStartX = useRef<number | null>(null);
    const touchEndX = useRef<number | null>(null);
    const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([]);

    // Combine cover and gallery images for full gallery view (no duplicates)
    const allImages = coverImage 
      ? [coverImage, ...images.filter(img => img !== coverImage)]
      : images;

    // Expose open method to parent
    useImperativeHandle(ref, () => ({
      open: (startIndex = 0) => {
        setCurrentIndex(Math.min(startIndex, allImages.length - 1));
        setIsOpen(true);
      },
    }));

    // Lock body scroll when lightbox is open
    useEffect(() => {
      if (isOpen) {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }
    }, [isOpen]);

    // Preload adjacent images
    useEffect(() => {
      if (isOpen && allImages.length > 1) {
        const preloadIndexes = [
          (currentIndex + 1) % allImages.length,
          (currentIndex - 1 + allImages.length) % allImages.length,
        ];
        preloadIndexes.forEach(idx => {
          const img = new Image();
          img.src = allImages[idx];
        });
      }
    }, [isOpen, currentIndex, allImages]);

    // Scroll active thumbnail into view
    useEffect(() => {
      if (isOpen && thumbnailRefs.current[currentIndex]) {
        thumbnailRefs.current[currentIndex]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }, [isOpen, currentIndex]);

    const handlePrev = useCallback(() => {
      setCurrentIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
    }, [allImages.length]);

    const handleNext = useCallback(() => {
      setCurrentIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
    }, [allImages.length]);

    // Keyboard navigation
    useEffect(() => {
      if (!isOpen) return;

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          handlePrev();
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          handleNext();
        }
        if (e.key === 'Escape') {
          setIsOpen(false);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handlePrev, handleNext]);

    // Touch handlers for swipe
    const handleTouchStart = (e: React.TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchEndX.current = null;
    };

    const handleTouchMove = (e: React.TouchEvent) => {
      touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
      if (touchStartX.current === null || touchEndX.current === null) return;
      
      const diff = touchStartX.current - touchEndX.current;
      const minSwipeDistance = 50;

      if (Math.abs(diff) > minSwipeDistance) {
        if (diff > 0) {
          handleNext(); // Swipe left = next
        } else {
          handlePrev(); // Swipe right = prev
        }
      }

      touchStartX.current = null;
      touchEndX.current = null;
    };

    if (allImages.length === 0) return null;

    const displayImages = images.slice(0, 3);
    const remainingCount = allImages.length - 3 - (coverImage ? 1 : 0);

    return (
      <>
        {/* Gallery Thumbnails Grid - Only show if enabled and has gallery images */}
        {showThumbnails && images.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {displayImages.map((img, idx) => (
              <button
                key={img}
                onClick={() => {
                  // Find index in allImages (accounting for cover)
                  const allIdx = allImages.indexOf(img);
                  setCurrentIndex(allIdx >= 0 ? allIdx : idx + (coverImage ? 1 : 0));
                  setIsOpen(true);
                }}
                className={cn(
                  "relative aspect-square rounded-lg overflow-hidden group focus:outline-none focus:ring-2 focus:ring-primary",
                  idx === 2 && remainingCount > 0 && "relative"
                )}
                aria-label={`Visa galleribild ${idx + 1}`}
              >
                <img
                  src={img}
                  alt={`Galleribild ${idx + 1}`}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                {idx === 2 && remainingCount > 0 && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                    <span className="text-white font-semibold text-lg">
                      +{remainingCount}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              </button>
            ))}
          </div>
        )}

        {/* Show All Button */}
        {showThumbnails && allImages.length > 3 && (
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            onClick={() => {
              setCurrentIndex(0);
              setIsOpen(true);
            }}
            aria-label="Se alla bilder i galleriet"
          >
            <Images className="h-4 w-4 mr-2" />
            Se alla {allImages.length} bilder
          </Button>
        )}

        {/* Lightbox Dialog */}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent 
            className="max-w-[95vw] lg:max-w-5xl w-full p-0 bg-black/95 border-none overflow-hidden"
            aria-describedby={undefined}
          >
            <div 
              className="relative flex flex-col min-h-[70vh] lg:min-h-[80vh]"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {/* Close Button */}
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-3 right-3 text-white hover:bg-white/20 z-20"
                onClick={() => setIsOpen(false)}
                aria-label="Stäng galleri"
              >
                <X className="h-6 w-6" />
              </Button>

              {/* Main Image Area */}
              <div className="flex-1 flex items-center justify-center relative px-4 py-12 lg:px-16">
                {/* Navigation Arrows - Desktop */}
                {allImages.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute left-2 lg:left-4 text-white hover:bg-white/20 z-10 hidden sm:flex"
                      onClick={handlePrev}
                      aria-label="Föregående bild"
                    >
                      <ChevronLeft className="h-8 w-8" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-2 lg:right-4 text-white hover:bg-white/20 z-10 hidden sm:flex"
                      onClick={handleNext}
                      aria-label="Nästa bild"
                    >
                      <ChevronRight className="h-8 w-8" />
                    </Button>
                  </>
                )}

                {/* Current Image */}
                <img
                  src={allImages[currentIndex]}
                  alt={`Bild ${currentIndex + 1} av ${allImages.length}`}
                  className="max-h-[60vh] lg:max-h-[70vh] max-w-full object-contain rounded-lg"
                />

                {/* Image Counter */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-3 py-1 rounded-full">
                  {currentIndex + 1} / {allImages.length}
                </div>
              </div>

              {/* Thumbnail Strip */}
              {allImages.length > 1 && (
                <div className="bg-black/80 border-t border-white/10">
                  <ScrollArea className="w-full">
                    <div className="flex gap-2 p-3 justify-center min-w-max">
                      {allImages.map((img, idx) => (
                        <button
                          key={`thumb-${idx}`}
                          ref={el => thumbnailRefs.current[idx] = el}
                          onClick={() => setCurrentIndex(idx)}
                          className={cn(
                            "w-14 h-14 lg:w-16 lg:h-16 rounded-md overflow-hidden flex-shrink-0 border-2 transition-all focus:outline-none focus:ring-2 focus:ring-white",
                            idx === currentIndex 
                              ? "border-white ring-1 ring-white" 
                              : "border-transparent opacity-50 hover:opacity-100"
                          )}
                          aria-label={`Gå till bild ${idx + 1}`}
                          aria-current={idx === currentIndex ? 'true' : 'false'}
                        >
                          <img
                            src={img}
                            alt=""
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ))}
                    </div>
                    <ScrollBar orientation="horizontal" className="h-2" />
                  </ScrollArea>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  }
);

EventGalleryLightbox.displayName = 'EventGalleryLightbox';
